import ReviewContextHeader from '../components/document-detail/ReviewContextHeader';
import ReviewWorkspace from '../components/document-detail/ReviewWorkspace';
import DecisionDialog from '../components/document-detail/DecisionDialog';
import { DocumentDetail, validMonth, periodLabel, filePeriod, allowedActions } from '../components/document-detail/detailModel';
import { getReviewReturnTo, returnLabel } from '../components/review/reviewModel';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { notificationService } from '../services/notificationService';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Calendar,
  AlertCircle,
  MessageSquare,
  RefreshCw,
  Eye,
  Trash2,
  CalendarDays,
  List,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getExpectedMonthsForYear, getPeriodDateRange, getPeriodStartMonth, parseIntervalMonths } from '../utils/periodUtils';
import {
  autoFillPhantomsAfterApproval,
  fetchOverridesForUser,
  getOverrideLabel,
  isOverrideApplicable,
  UserRequirementOverride,
} from '../utils/overrideUtils';
import { fetchMonthlyZip, assembleAnnualZip, runAnnualFanOut, MonthlyZipResult } from '../utils/annualZipAssembler';

interface UserDetail {
  id: string;
  email: string;
  full_name: string | null;
  is_moral: boolean;
  organization: {
    id: string;
    name: string;
  } | null;
  primaryOrganization: {
    id: string;
    name: string;
  } | null;
  user_extra_mails?: Array<{
    id: string;
    email: string | null;
  }>;
}

interface MonthlyDocumentStatus {
  requirementId: string;
  documentName: string;
  categoryName: string;
  renewalType?: string | null;
  renewalInterval?: any;
  dueDate?: string | null;
  isMoral?: boolean;
  monthStatuses: Record<string, {
    status: 'not_uploaded' | 'pending_approval' | 'approved' | 'rejected';
    documentId?: string;
    uploadDate?: string;
    rejectionReason?: string | null;
  }>;
}

export default function UserDocumentDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queueReturnTo = getReviewReturnTo(searchParams.get('returnTo'));
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const legacyState = location.state as { selectedMonth?: string; documentId?: string } | null;
  const routeMonth = searchParams.get('selectedMonth') || legacyState?.selectedMonth || null;
  const routeDocumentId = searchParams.get('documentId') || legacyState?.documentId || null;
  const routeView = searchParams.get('view');
  const routeYear = searchParams.get('year');
  const detailRequest = useRef(0);
  const monthRequest = useRef(0);
  const mutationLock = useRef(false);
  const activeUserId = useRef(userId);
  activeUserId.current = userId;
  const [detailError, setDetailError] = useState<string | null>(null);
  const [monthError, setMonthError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [decisionMessage, setDecisionMessage] = useState<string | null>(null);

  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [documents, setDocuments] = useState<DocumentDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingDoc, setProcessingDoc] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DocumentDetail | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionAiSummary,setRejectionAiSummary]=useState<string>();
  const [isRevisionRequest, setIsRevisionRequest] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState<any[]>([]);
  const [showNotificationHistory, setShowNotificationHistory] = useState(false);
  const [monthlyDocumentStatuses, setMonthlyDocumentStatuses] = useState<MonthlyDocumentStatus[]>([]);
  const [documentDefinitions, setDocumentDefinitions] = useState<any[]>([]);
  const [userRequirements, setUserRequirements] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'list'>('table');
  const [showPendingList, setShowPendingList] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(Math.max(0, new Date().getMonth() - 1));
  const [calendarSearch, setCalendarSearch] = useState('');
  const [calendarOffset, setCalendarOffset] = useState(0); // 0 = current period, -1 = previous year, etc.
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [documentToApprove, setDocumentToApprove] = useState<DocumentDetail | null>(null);
  const [overrides, setOverrides] = useState<UserRequirementOverride[]>([]);
  const [overrideModalDef, setOverrideModalDef] = useState<any | null>(null);
  const [overrideForm, setOverrideForm] = useState({ renewal_type: 'custom_interval', interval_months: '3' });
  const [savingOverride, setSavingOverride] = useState(false);
  const [monthFilteredRequirements, setMonthFilteredRequirements] = useState<any[]>([]);
  const [loadingMonthData, setLoadingMonthData] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const _today = new Date();
  const _currentYear = _today.getFullYear();
  const _currentMonth = _today.getMonth() + 1;
  const _defaultMonth = _currentMonth === 1 ? 12 : _currentMonth - 1;
  const _defaultYear = _currentMonth === 1 ? _currentYear - 1 : _currentYear;
  const [zipYear, setZipYear] = useState<number>(_defaultYear);
  const [zipMonth, setZipMonth] = useState<number>(_defaultMonth);
  const [zipMode, setZipMode] = useState<'monthly' | 'annual'>('monthly');
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number; phase: 'downloading' | 'assembling' } | null>(null);
  const zipAbortRef = useRef<AbortController | null>(null);

  const handleDownloadZip = async () => {
    if (!userId || downloadingZip) return;
    const url = process.env.REACT_APP_ZIP_FUNCTION_URL;
    if (!url) {
      alert('Falta configurar REACT_APP_ZIP_FUNCTION_URL');
      return;
    }

    const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    const firstName = (userDetail?.full_name || 'Contratista').trim().split(/\s+/)[0].replace(/[/\\?%*:|"<>\s]/g, '');
    const controller = new AbortController();
    zipAbortRef.current = controller;

    try {
      setDownloadingZip(true);
      let { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sesión no disponible');
      if (session.expires_at && session.expires_at * 1000 < Date.now() + 60_000) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed.session) session = refreshed.session;
      }

      if (zipMode === 'monthly') {
        const result = await fetchMonthlyZip({
          url,
          contractorUserId: userId,
          year: zipYear,
          month: zipMonth,
          accessToken: session.access_token,
          signal: controller.signal,
        });
        if (!result.blob || result.fileCount === 0) {
          alert('No hay documentos para ese mes.');
          setShowZipModal(false);
          return;
        }
        const displayFilename = `${firstName}${MONTHS_ES[zipMonth - 1]}${zipYear}.zip`;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(result.blob);
        a.download = displayFilename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        setShowZipModal(false);
        return;
      }

      // Modo anual: fan-out mensual con concurrencia limitada + ensamble local.
      setZipProgress({ done: 0, total: 12, phase: 'downloading' });
      const { results, failed } = await runAnnualFanOut({
        url,
        contractorUserId: userId,
        year: zipYear,
        accessToken: session.access_token,
        signal: controller.signal,
        onProgress: (done) => setZipProgress(p => (p ? { ...p, done } : p)),
      });

      const withContent = results.filter((r): r is MonthlyZipResult => !!r && !!r.blob && r.fileCount > 0);
      if (withContent.length === 0) {
        alert('No hay documentos para ese año.');
        setShowZipModal(false);
        return;
      }

      await assembleAnnualZip(withContent, {
        outputName: `${firstName}Anual${zipYear}.zip`,
        onPhaseChange: (phase) => setZipProgress(p => (p ? { ...p, phase } : p)),
      });

      if (failed.length) {
        alert(`ZIP anual generado con ${12 - failed.length}/12 meses. Fallaron: ${failed.map(m => MONTHS_ES[m - 1]).join(', ')}.`);
      }
      setShowZipModal(false);
    } catch (err: any) {
      if (err?.name !== 'AbortError') alert(`Error al generar ZIP: ${err.message || err}`);
    } finally {
      setDownloadingZip(false);
      setZipProgress(null);
      zipAbortRef.current = null;
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let active = true;
    setUserDetail(null); setDocuments([]); setMonthFilteredRequirements([]);
    setShowApprovalDialog(false); setShowRejectDialog(false); setDecisionMessage(null);
    if (userId) {
      fetchUserDetails();
      fetchOverridesForUser(userId).then(data => { if (active) setOverrides(data); }).catch(() => { if (active) setOverrides([]); });
    }
    return () => { active = false; detailRequest.current++; monthRequest.current++; };
  }, [userId]);

  const openOverrideModal = (definition: any) => {
    const existing = overrides.find(o => o.definition_id === definition.id);
    if (existing) {
      setOverrideForm({
        renewal_type: existing.renewal_type,
        interval_months: existing.renewal_type === 'custom_interval'
          ? String(parseIntervalMonths(existing.renewal_interval))
          : '3',
      });
    } else {
      setOverrideForm({ renewal_type: 'custom_interval', interval_months: '3' });
    }
    setOverrideModalDef(definition);
  };

  const handleSaveOverride = async () => {
    if (!userId || !overrideModalDef) return;

    const intervalMonths = parseInt(overrideForm.interval_months, 10);
    if (overrideForm.renewal_type === 'custom_interval' && (!intervalMonths || intervalMonths < 2 || intervalMonths > 12)) {
      alert('El intervalo debe ser entre 2 y 12 meses');
      return;
    }

    const candidate = {
      renewal_type: overrideForm.renewal_type as UserRequirementOverride['renewal_type'],
      renewal_interval: overrideForm.renewal_type === 'custom_interval' ? `${intervalMonths} mons` : null,
    };

    if (!isOverrideApplicable(candidate, overrideModalDef.renewal_type, overrideModalDef.renewal_interval)) {
      alert('La periodicidad personalizada debe ser menos frecuente que la periodicidad del documento');
      return;
    }

    try {
      setSavingOverride(true);
      const { error } = await supabase
        .from('user_requirement_overrides')
        .upsert({
          user_id: userId,
          definition_id: overrideModalDef.id,
          renewal_type: candidate.renewal_type,
          renewal_interval: candidate.renewal_interval,
          created_by_user_id: user?.id || null,
        } as any, { onConflict: 'user_id,definition_id' });

      if (error) throw error;

      setOverrides(await fetchOverridesForUser(userId));
      setOverrideModalDef(null);
      alert('Periodicidad personalizada guardada');
    } catch (error) {
      console.error('Error saving override:', error);
      alert('Error al guardar la periodicidad personalizada');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleRemoveOverride = async () => {
    if (!userId || !overrideModalDef) return;
    try {
      setSavingOverride(true);
      const { error } = await supabase
        .from('user_requirement_overrides')
        .delete()
        .eq('user_id', userId)
        .eq('definition_id', overrideModalDef.id);

      if (error) throw error;

      setOverrides(await fetchOverridesForUser(userId));
      setOverrideModalDef(null);
      alert('Periodicidad personalizada eliminada');
    } catch (error) {
      console.error('Error removing override:', error);
      alert('Error al eliminar la periodicidad personalizada');
    } finally {
      setSavingOverride(false);
    }
  };

  useEffect(() => {
    if (routeView === 'calendar' || !routeMonth) {
      if (routeYear && /^\d{4}$/.test(routeYear) && Number(routeYear) >= 2015) setCalendarOffset(Number(routeYear) - new Date().getFullYear());
      setViewMode('table'); setSelectedMonth(null); monthRequest.current++;
      return;
    }
    setViewMode('list');
    if (!validMonth(routeMonth)) {
      setSelectedMonth(null); setMonthError('El periodo del enlace no es válido. Selecciona un mes para continuar.');
      setMonthFilteredRequirements([]); setLoadingMonthData(false); monthRequest.current++;
      return;
    }
    setSelectedMonth(routeMonth);
    setCalendarOffset(Number(routeMonth.slice(0, 4)) - new Date().getFullYear());
    fetchMonthFilteredRequirements(routeMonth);
  }, [routeMonth, routeView, routeYear, userId]);

  useEffect(() => {
    if (documents.length > 0 && documentDefinitions.length > 0) {
      generateMonthlyDocumentStatuses(documents, documentDefinitions, userRequirements, calendarOffset);
    }
  }, [calendarOffset, documents, documentDefinitions, userRequirements, userDetail]);

  const fetchUserDetails = async () => {
    if (!userId) return;
    const requestId = ++detailRequest.current;
    setDetailError(null);
    
    try {
      setLoading(true);

      // Fetch user details
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          *,
          user_organization!left(
            organization:organizations(id, name),
            is_active,
            is_main
          ),
          user_extra_mails(id, email)
        `)
        .eq('id', userId)
        .single();

      if (requestId !== detailRequest.current) return;
      if (userError || !userData) throw userError || new Error('Usuario no disponible');
      
      // Get user's primary organization or any active organization
      const primaryOrg = userData.user_organization?.find((uo: any) => uo.is_active && uo.is_main);
      const activeOrg = userData.user_organization?.find((uo: any) => uo.is_active);
      const userOrg = primaryOrg?.organization || activeOrg?.organization || null;
      
      setUserDetail({
        ...userData,
        organization: userOrg,
        primaryOrganization: primaryOrg?.organization || null
      });

      // Fetch user's documents - get requirements first, then documents
      const { data: requirementsData, error: reqError } = await supabase
        .from('user_document_requirements')
        .select(`
          *,
          definition:document_definitions(
            name,
            description,
            renewal_type,
            renewal_interval,
            category:categories(name)
          ),
          documents:documents!documents_requirement_id_fkey(*)
        `)
        .eq('user_id', userId)
        .order('due_date', { ascending: true });

      if (requestId !== detailRequest.current) return;
      if (reqError) throw reqError;
      
      // Extract all documents from requirements and format them
      const allDocuments: DocumentDetail[] = [];
      (requirementsData || []).forEach((req: any) => {
        if (req.documents && req.documents.length > 0) {
          req.documents.forEach((doc: any) => {
            if (!doc.is_deleted) {
              allDocuments.push({
                ...doc,
                requirement: {
                  id: req.id,
                  status: req.status,
                  due_date: req.due_date,
                  user_id: req.user_id,
                  definition: req.definition
                }
              });
            }
          });
        }
      });
      
      // Sort documents by upload_date descending
      allDocuments.sort((a, b) => new Date(b.upload_date).getTime() - new Date(a.upload_date).getTime());
      
      // Filter out documents without proper requirement data
      const validDocuments = allDocuments.filter(
        doc => doc.requirement && doc.requirement.definition
      );
      setDocuments(validDocuments);
      
      // Fetch notification logs
      const { data: logsData } = await supabase
        .from('notification_logs')
        .select('*')
        .eq('user_id', userId)
        .order('email_sent_at', { ascending: false })
        .limit(20);
      
      if (requestId !== detailRequest.current) return;
      setNotificationLogs(logsData || []);

      // Fetch all document definitions
      const { data: definitionsData, error: definitionsError } = await supabase
        .from('document_definitions')
        .select(`
          id,
          name,
          description,
          is_active,
          is_moral,
          renewal_type,
          renewal_interval,
          category:categories(name)
        `)
        .eq('is_active', true)
        .order('name');

      if (requestId !== detailRequest.current) return;
      if (definitionsError) throw definitionsError;
      
      // Filter definitions based on user's is_moral status
      const filteredDefinitions = (definitionsData || []).filter(def => {
        // If user is moral, show documents with is_moral=true or null
        if (userData.is_moral === true) {
          return def.is_moral === true || def.is_moral === null;
        }
        // If user is physical, show documents with is_moral=false or null
        if (userData.is_moral === false) {
          return def.is_moral === false || def.is_moral === null;
        }
        // If user's is_moral is not set, show all
        return true;
      });
      
      setDocumentDefinitions(filteredDefinitions);
      
      // Store the requirements we already fetched for documents
      const simplifiedRequirements = (requirementsData || []).map((req: any) => ({
        id: req.id,
        definition_id: req.definition?.id,
        due_date: req.due_date,
        status: req.status
      }));
      
      // Store requirements in state
      setUserRequirements(simplifiedRequirements);

      // Generate monthly document statuses with requirements data
      // Coverage is derived in the effect using the latest selected year.

    } catch (error) {
      if (requestId === detailRequest.current) setDetailError('No se pudo cargar el expediente completo. Actualiza los datos para continuar.');
    } finally {
      if (requestId === detailRequest.current) setLoading(false);
    }
  };

  const fetchMonthFilteredRequirements = async (selectedMonth: string) => {
    if (!userId) return;
    
    if (!validMonth(selectedMonth)) return;
    const requestId = ++monthRequest.current;
    setMonthError(null);
    setMonthFilteredRequirements([]);
    
    try {
      setLoadingMonthData(true);
      
      const [year, month] = selectedMonth.split('-').map(Number);

      // Use the new vertical period search RPC
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_user_documents_by_month_vertical' as any, {
          p_user_id: userId,
          p_year: year,
          p_month: month
        }) as { data: any[] | null, error: any };

      if (requestId !== monthRequest.current) return;
      if (rpcError) throw rpcError;
      
      // Group by DEFINITION (not requirement) to show one card per document type
      const definitionsMap = new Map();

      (rpcData || []).forEach((row: any) => {
        const defId = row.dd_id;

        if (!definitionsMap.has(defId)) {
          // Create definition object
          definitionsMap.set(defId, {
            id: defId, // Use definition ID as the main ID
            definition_id: defId,
            due_date: row.udr_due_date, // Use first requirement's due_date
            status: row.udr_status || 'required',
            is_placeholder: !row.udr_id,
            udr_is_current: row.udr_is_current,
            definition: {
              id: row.dd_id,
              name: row.dd_name,
              description: row.dd_description,
              renewal_type: row.dd_renewal_type,
              renewal_interval: row.dd_renewal_interval,
              is_moral: row.dd_is_moral,
              is_active: row.dd_is_active,
              is_required: row.dd_is_required,
              category: { name: row.dd_category_name || null },
              metadata: row.dd_metadata
            },
            documents: []
          });
        } else {
          // If this row has is_current=true, prefer its data for the card
          const existing = definitionsMap.get(defId);
          if (row.udr_is_current === true && existing.udr_is_current !== true) {
            existing.status = row.udr_status || 'required';
            existing.due_date = row.udr_due_date;
            existing.udr_is_current = true;
          }
        }

        // Add document if exists and not already added
        if (row.doc_id && !row.doc_is_deleted) {
          const def = definitionsMap.get(defId);
          const docExists = def.documents.some((d: any) => d.id === row.doc_id);

          if (!docExists) {
            def.documents.push({
              id: row.doc_id,
              requirement_id: row.doc_requirement_id,
              file_location: row.doc_file_location,
              original_filename: row.doc_original_filename,
              mime_type: row.doc_mime_type,
              file_size_bytes: row.doc_file_size_bytes,
              issue_date: row.doc_issue_date,
              expiry_date: row.doc_expiry_date,
              status: row.doc_status,
              upload_date: row.doc_upload_date,
              approved_by_user_id: row.doc_approved_by_user_id,
              approval_timestamp: row.doc_approval_timestamp,
              rejection_reason: row.doc_rejection_reason,
              applicable_month: row.doc_applicable_month,
              applicable_year: row.doc_applicable_year,
              is_deleted: row.doc_is_deleted,
              created_at: row.doc_created_at,
              updated_at: row.doc_updated_at,
              metadata: row.doc_metadata,
              udr_is_current: row.udr_is_current,
              udr_status: row.udr_status,
              udr_due_date: row.udr_due_date
            });
          }
        }
      });

      const requirementsWithDocs = Array.from(definitionsMap.values());
      // const totalDocuments = requirementsWithDocs.reduce((sum, req) => sum + req.documents.length, 0);
      
      // console.log(`Month ${selectedMonth}: Found ${definitionsSet.size} definitions, ${requirementsWithDocs.length} requirements (${requirementsWithDocs.filter(r => !r.is_placeholder).length} real, ${requirementsWithDocs.filter(r => r.is_placeholder).length} placeholders), ${totalDocuments} documents`);
      
      setMonthFilteredRequirements(requirementsWithDocs);
      
    } catch (error) {
      if (requestId === monthRequest.current) setMonthError('No se pudieron cargar los documentos de este periodo. Reintenta antes de tomar una decisión.');
    } finally {
      if (requestId === monthRequest.current) setLoadingMonthData(false);
    }
  };

  const generateMonthlyDocumentStatuses = (docs: DocumentDetail[], definitions: any[], requirements: any[], offset: number = 0) => {
    // Generate 12 months for the current year based on offset
    const months: string[] = [];
    const currentYear = new Date().getFullYear() + offset;
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, i, 1);
      months.push(format(date, 'yyyy-MM'));
    }

    // Create a map of documents by definition name
    const documentsByDefinition = new Map<string, DocumentDetail[]>();
    
    docs.forEach(doc => {
      if (!doc.requirement || !doc.requirement.definition) return;
      const defName = doc.requirement.definition.name;
      if (!documentsByDefinition.has(defName)) {
        documentsByDefinition.set(defName, []);
      }
      documentsByDefinition.get(defName)!.push(doc);
    });

    // Create monthly status data for ALL definitions
    const statusData: MonthlyDocumentStatus[] = [];
    
    definitions.forEach((definition) => {
      // Filter documents based on user's is_moral status
      // If user is_moral=true: show documents with is_moral=true or null
      // If user is_moral=false: show documents with is_moral=false or null
      if (userDetail) {
        if (userDetail.is_moral === true) {
          // User is moral: skip documents that are explicitly for physical persons (is_moral=false)
          if (definition.is_moral === false) return;
        } else if (userDetail.is_moral === false) {
          // User is physical: skip documents that are explicitly for moral persons (is_moral=true)
          if (definition.is_moral === true) return;
        }
      }
  

      const monthStatuses: Record<string, { status: 'not_uploaded' | 'pending_approval' | 'approved' | 'rejected'; documentId?: string; uploadDate?: string; rejectionReason?: string | null }> = {};
      
      // Initialize all months as not_uploaded
      months.forEach(month => {
        monthStatuses[month] = { status: 'not_uploaded' };
      });

      // Get documents for this definition
      const definitionDocs = documentsByDefinition.get(definition.name) || [];
      
      // Get due_date from requirement or from documents
      let dueDate: string | null = null;
      
      // First try to get due_date from user's requirement for this definition
      const userRequirement = requirements.find(req => req.definition_id === definition.id);
      if (userRequirement && userRequirement.due_date) {
        dueDate = userRequirement.due_date;
      } 
      // Fallback to document's requirement due_date if available
      else if (definitionDocs.length > 0 && definitionDocs[0].requirement?.due_date) {
        dueDate = definitionDocs[0].requirement.due_date;
      }
      
      // Update status based on documents
      definitionDocs.forEach(doc => {
        let documentMonth: string;
        
        // Use applicable_month/year if available, otherwise fall back to upload_date
        if (doc.applicable_month && doc.applicable_year) {
          const monthNum = monthAbbrevToNumber(doc.applicable_month);
          if (monthNum) {
            documentMonth = `${doc.applicable_year}-${monthNum.toString().padStart(2, '0')}`;
          } else {
            // Fallback to upload_date if month conversion fails
            documentMonth = format(new Date(doc.upload_date), 'yyyy-MM');
          }
        } else {
          // Fallback to upload_date if applicable month/year not set
          documentMonth = format(new Date(doc.upload_date), 'yyyy-MM');
        }
        
        if (monthStatuses[documentMonth]) {
          const currentMonthStatus = monthStatuses[documentMonth];

          // Priority logic: approved > pending_approval > rejected > reemplazado
          // reemplazado is like a soft delete - document was replaced before approval
          const statusPriority: Record<string, number> = {
            'approved': 4,
            'pending_approval': 2,
            'rejected': 1,
            'reemplazado': 0,  // Should not count - same as not_uploaded
            'not_uploaded': 0
          };
          const currentPriority = statusPriority[currentMonthStatus.status] || 0;
          const newPriority = statusPriority[doc.status] || 0;

          // Update if: no document yet, OR new document has better status, OR same status but newer
          if (!currentMonthStatus.uploadDate ||
              newPriority > currentPriority ||
              (newPriority === currentPriority && new Date(doc.upload_date) > new Date(currentMonthStatus.uploadDate!))) {
            monthStatuses[documentMonth] = {
              status: doc.status as 'pending_approval' | 'approved' | 'rejected',
              documentId: doc.id,
              uploadDate: doc.upload_date,
              rejectionReason: doc.rejection_reason
            };
          }
        }
      });

      
      statusData.push({
        requirementId: definition.id,
        documentName: definition.name,
        categoryName: definition.category?.name || 'Sin categoría',
        renewalType: definition.renewal_type,
        renewalInterval: definition.renewal_interval,
        dueDate: dueDate,
        isMoral: definition.is_moral,
        monthStatuses
      });
    });

    // Sort by category name first, then by document name
    statusData.sort((a, b) => {
      const categoryCompare = a.categoryName.localeCompare(b.categoryName);
      if (categoryCompare !== 0) return categoryCompare;
      return a.documentName.localeCompare(b.documentName);
    });

    setMonthlyDocumentStatuses(statusData);
  };

  const handleApprove = async (doc: DocumentDetail) => {
    
    if (!doc.requirement_id) {
      alert('Error: Información del requerimiento no disponible');
      return;
    }
    
    if (mutationLock.current) return false;
    if (!user?.id || !allowedActions(doc, userProfile?.role, []).approve || detailError || monthError || loadingMonthData) {
      setDecisionError('Esta versión ya no está disponible para esta acción. Actualiza el expediente.');
      return false;
    }
    mutationLock.current = true;
    setDecisionError(null);
    setDecisionMessage(null);
    try {
      setProcessingDoc(doc.id);

      // Update document status
      const { error: docError } = await supabase
        .from('documents')
        .update({
          status: 'approved',
          approval_timestamp: new Date().toISOString(),
          approved_by_user_id: user?.id
        })
        .eq('id', doc.id)
        .eq('requirement_id', doc.requirement_id)
        .eq('file_location', doc.file_location)
        .eq('status', 'pending_approval')
        .eq('is_deleted', false)
        .select('id')
        .single();

      if (docError) throw docError;

      // Update requirement status
      const { error: reqError } = await supabase
        .from('user_document_requirements')
        .update({
          status: 'fulfilled'
        })
        .eq('id', doc.requirement_id);

      if (reqError) throw reqError;

      // Auto-fill remaining periods with phantom documents when the user has
      // a periodicity override for this definition
      try {
        const phantomsCreated = await autoFillPhantomsAfterApproval(doc.id, user?.id);
        if (phantomsCreated > 0) {
          console.log(`Periodicidad personalizada: ${phantomsCreated} periodo(s) rellenados automáticamente`);
        }
      } catch (phantomFillError) {
        console.error('Error auto-filling phantom documents:', phantomFillError);
      }

      if (activeUserId.current === userId) setDecisionMessage('Aprobación guardada.');

      // Send approval notification with user's primary organization
      try {
        const userPrimaryOrg = userDetail?.primaryOrganization || userDetail?.organization;
        const additionalEmails = userDetail?.user_extra_mails
          ?.filter(mail => mail.email)
          ?.map(mail => mail.email as string) || [];
        
        await notificationService.sendStatusChangeNotification(
          doc.id, 
          'approved',
          userPrimaryOrg?.id,
          userPrimaryOrg?.name,
          additionalEmails
        );
      } catch (emailError) {
        if (activeUserId.current === userId) setDecisionMessage('Aprobación guardada. No se pudo confirmar el envío de la notificación.');
        console.error('Error sending approval notification:', emailError);
      }

      // Refresh data
      if (activeUserId.current !== userId) return true;
      await fetchUserDetails();
      
      // If in list view, also refresh month filtered requirements
      if (activeUserId.current === userId && viewMode === 'list' && selectedMonth) {
        await fetchMonthFilteredRequirements(selectedMonth);
      }
      
      return true;
    } catch (error) {
      console.error('Error approving document:', error);
      setDecisionError('No se completó la aprobación. El estado puede haber cambiado. Cierra este diálogo y actualiza el expediente antes de reintentar.');
      return false;
    } finally {
      mutationLock.current = false;
      setProcessingDoc(null);
    }
  };

  const handleRequestRevision = async () => {
    if (!selectedDoc || !rejectionReason.trim()) return;
    
    if (!selectedDoc.requirement_id) {
      alert('Error: Información del requerimiento no disponible');
      return;
    }

    if (mutationLock.current) return false;
    if (!user?.id || !allowedActions(selectedDoc, userProfile?.role, []).revision || detailError || monthError || loadingMonthData) {
      setDecisionError('Esta versión ya no está disponible para esta acción. Actualiza el expediente.');
      return false;
    }
    mutationLock.current = true;
    setDecisionError(null);
    setDecisionMessage(null);
    try {
      setProcessingDoc(selectedDoc.id);
      
      const { error: docError } = await supabase
        .from('documents')
        .update({
          status: 'pending_approval',
          approved_by_user_id: null,
          approval_timestamp: null,
          rejection_reason: `SOLICITUD DE REVISIÓN: ${rejectionReason}`
        })
        .eq('id', selectedDoc.id)
        .eq('requirement_id', selectedDoc.requirement_id)
        .eq('file_location', selectedDoc.file_location)
        .eq('status', 'approved')
        .eq('is_deleted', false)
        .select('id')
        .single();

      if (docError) throw docError;

      // Update requirement status back to required if it was fulfilled
      // First, get the current requirement status
      const { data: requirementData, error: fetchReqError } = await supabase
        .from('user_document_requirements')
        .select('status')
        .eq('id', selectedDoc.requirement_id)
        .single();

      if (fetchReqError) {
        throw fetchReqError;
      } else if (requirementData && requirementData.status === 'fulfilled') {
        const { error: reqError } = await supabase
          .from('user_document_requirements')
          .update({
            status: 'required'
          })
          .eq('id', selectedDoc.requirement_id);

        if (reqError) {
          throw reqError;
        }
      }

      if (activeUserId.current === userId) setDecisionMessage('Solicitud de revisión guardada.');

      // Send revision request notification
      try {
        const userPrimaryOrg = userDetail?.primaryOrganization || userDetail?.organization;
        const additionalEmails = userDetail?.user_extra_mails
          ?.filter(mail => mail.email)
          ?.map(mail => mail.email as string) || [];
        
        await notificationService.sendStatusChangeNotification(
          selectedDoc.id, 
          'revision_requested',
          userPrimaryOrg?.id,
          userPrimaryOrg?.name,
          additionalEmails
        );
      } catch (emailError) {
        if (activeUserId.current === userId) setDecisionMessage('Solicitud de revisión guardada. No se pudo confirmar el envío de la notificación.');
        console.error('Error sending revision request notification:', emailError);
      }

      // Reset dialog state first (before refresh to avoid showing error on success)
      setShowRejectDialog(false);
      setSelectedDoc(null);
      setRejectionReason('');
      setIsRevisionRequest(false);

      // Refresh data
      try {
        if (activeUserId.current !== userId) return true;
      await fetchUserDetails();

        // If in list view, also refresh month filtered requirements
        if (activeUserId.current === userId && viewMode === 'list' && selectedMonth) {
          await fetchMonthFilteredRequirements(selectedMonth);
        }
      } catch (refreshError) {
        console.error('Error refreshing data after revision request:', refreshError);
        // Don't show error alert as the main operation succeeded
      }

      return true;
    } catch (error) {
      console.error('Error requesting revision:', error);
      setDecisionError('No se completó la solicitud. Cierra este diálogo y actualiza el expediente antes de reintentar.');
      return false;
    } finally {
      mutationLock.current = false;
      setProcessingDoc(null);
    }
  };

  const handleReject = async (message?:string) => {
    const chosenReason=message ?? rejectionReason;
    if (!selectedDoc || !chosenReason.trim()) return;

    if (!selectedDoc.requirement_id) {
      alert('Error: Información del requerimiento no disponible');
      return;
    }

    if (mutationLock.current) return false;
    if (!user?.id || !allowedActions(selectedDoc, userProfile?.role, []).reject || detailError || monthError || loadingMonthData) {
      setDecisionError('Esta versión ya no está disponible para esta acción. Actualiza el expediente.');
      return false;
    }
    mutationLock.current = true;
    setDecisionError(null);
    setDecisionMessage(null);
    try {
      setProcessingDoc(selectedDoc.id);

      const finalReason = isRevisionRequest 
        ? `SOLICITUD DE REVISIÓN: ${chosenReason}`
        : chosenReason;

      // Update document status
      const { error: docError } = await supabase
        .from('documents')
        .update({
          status: 'rejected',
          approval_timestamp: new Date().toISOString(),
          approved_by_user_id: user?.id,
          rejection_reason: finalReason
        })
        .eq('id', selectedDoc.id)
        .eq('requirement_id', selectedDoc.requirement_id)
        .eq('file_location', selectedDoc.file_location)
        .eq('status', 'pending_approval')
        .eq('is_deleted', false)
        .select('id')
        .single();

      if (docError) throw docError;

      // Update requirement status back to required
      const { error: reqError } = await supabase
        .from('user_document_requirements')
        .update({
          status: 'required'
        })
        .eq('id', selectedDoc.requirement_id);

      if (reqError) throw reqError;

      if (activeUserId.current === userId) setDecisionMessage('Rechazo guardado.');

      // Send rejection notification with user's primary organization
      try {
        const userPrimaryOrg = userDetail?.primaryOrganization || userDetail?.organization;
        const additionalEmails = userDetail?.user_extra_mails
          ?.filter(mail => mail.email)
          ?.map(mail => mail.email as string) || [];
        
        const notified=await notificationService.sendStatusChangeNotification(
          selectedDoc.id, 
          'rejected',
          userPrimaryOrg?.id,
          userPrimaryOrg?.name,
          additionalEmails
        );
        if(!notified&&activeUserId.current===userId)setDecisionMessage('Rechazo guardado. No se pudo confirmar el envío de la notificación.');
      } catch (emailError) {
        if (activeUserId.current === userId) setDecisionMessage('Rechazo guardado. No se pudo confirmar el envío de la notificación.');
        console.error('Error sending rejection notification:', emailError);
      }

      // Refresh data
      if (activeUserId.current !== userId) return true;
      await fetchUserDetails();
      
      // If in list view, also refresh month filtered requirements
      if (activeUserId.current === userId && viewMode === 'list' && selectedMonth) {
        await fetchMonthFilteredRequirements(selectedMonth);
      }

      // Reset dialog after data is refreshed
      setShowRejectDialog(false);
      setSelectedDoc(null);
      setRejectionReason('');
      setIsRevisionRequest(false);
      
      return true;
    } catch (error) {
      console.error('Error rejecting document:', error);
      setDecisionError('No se completó el rechazo. El estado puede haber cambiado. Cierra este diálogo y actualiza el expediente antes de reintentar.');
      return false;
    } finally {
      mutationLock.current = false;
      setProcessingDoc(null);
    }
  };

  const handleDownload = async (doc: DocumentDetail) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(doc.file_location);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_location.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error al descargar el documento');
    }
  };

  const handleDelete = async (doc: DocumentDetail) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este documento? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setProcessingDoc(doc.id);
      
      // Soft delete the document
      const { error } = await supabase
        .from('documents')
        .update({ is_deleted: true })
        .eq('id', doc.id);

      if (error) throw error;

      // Update requirement status back to required if needed
      if (doc.requirement && doc.requirement.id) {
        await supabase
          .from('user_document_requirements')
          .update({ status: 'required' })
          .eq('id', doc.requirement.id);
      }

      // Refresh documents
      await fetchUserDetails();
      if (selectedMonth) await fetchMonthFilteredRequirements(selectedMonth);
      alert('Documento eliminado exitosamente');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error al eliminar el documento');
    } finally {
      setProcessingDoc(null);
    }
  };

  const handleDiscardDocument = async (documentId: string) => {
    if (!window.confirm('¿Descartar este documento? Se marcará como reemplazado ya que existe otro documento aprobado.')) {
      return;
    }

    try {
      setProcessingDoc(documentId);

      const { error } = await supabase
        .from('documents')
        .update({ status: 'reemplazado' as any })
        .eq('id', documentId);

      if (error) throw error;

      // Refresh data
      await fetchUserDetails();
      if (viewMode === 'list' && selectedMonth) {
        await fetchMonthFilteredRequirements(selectedMonth);
      }

      alert('Documento descartado exitosamente');
    } catch (error) {
      console.error('Error discarding document:', error);
      alert('Error al descartar el documento');
    } finally {
      setProcessingDoc(null);
    }
  };

  const handleCreatePhantomSUA = async (requirement: any) => {
    try {
      setProcessingDoc('phantom-' + requirement.id);

      // Get current month and year from selectedMonth
      const [year, month] = selectedMonth!.split('-').map(Number);
      const monthAbbrevs = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                            'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

      // Get document name from definition
      const definitionName = requirement.definition?.name || 'DOCUMENTO';
      const docType = definitionName.toUpperCase().replace(/\s+/g, '_');

      // Use definition name for file extension and folder
      const fileExtension = definitionName.toUpperCase().replace(/\s+/g, '_').substring(0, 10);
      const folderName = definitionName.toLowerCase().replace(/\s+/g, '_');

      let requirementId = requirement.id;

      // Check if this is a placeholder (id === definition_id means no real requirement exists)
      const isPlaceholder = requirement.id === requirement.definition_id;

      // If this is a placeholder, find or create the requirement first
      if (isPlaceholder) {
        if (!userId) throw new Error('User ID not found');

        // Compute the period date range for the selected month, matching how
        // the user upload flow assigns due_date (last day of the period)
        const renewalType = requirement.definition?.renewal_type;
        const renewalInterval = requirement.definition?.renewal_interval;
        let effectiveRenewalType = renewalType;
        if (renewalInterval === '1 mons') effectiveRenewalType = 'monthly';
        if (renewalInterval === '12 mons') effectiveRenewalType = 'yearly';

        const periodMonth = effectiveRenewalType === 'custom_interval'
          ? getPeriodStartMonth(month, parseIntervalMonths(renewalInterval))
          : month;

        const { startDate: periodStart, endDate: periodEnd } = getPeriodDateRange(
          effectiveRenewalType,
          renewalInterval,
          year,
          periodMonth
        );
        const periodStartStr = format(periodStart, 'yyyy-MM-dd');
        const periodEndStr = format(periodEnd, 'yyyy-MM-dd');

        // Find an existing requirement for this period. Requirements created by
        // the upload flow have is_current=false, so never filter by is_current
        const { data: existingReqs, error: findError } = await supabase
          .from('user_document_requirements')
          .select('id')
          .eq('user_id', userId!)
          .eq('definition_id', requirement.definition_id)
          .gte('due_date', periodStartStr)
          .lte('due_date', periodEndStr)
          .order('assigned_at', { ascending: true })
          .limit(1);

        if (findError) throw findError;

        if (existingReqs && existingReqs.length > 0) {
          // Requirement already exists, use its ID
          requirementId = existingReqs[0].id;
        } else {
          // Create new requirement with the period's due date so the upload
          // flow and dashboard queries can find it later
          const { data: newRequirement, error: reqCreateError } = await supabase
            .from('user_document_requirements')
            .insert({
              user_id: userId!,
              definition_id: requirement.definition_id,
              due_date: periodEndStr,
              status: 'fulfilled',
              assigned_at: new Date().toISOString(),
              is_current: false
            })
            .select()
            .single();

          if (reqCreateError) throw reqCreateError;
          requirementId = newRequirement.id;
        }
      }

      // Create phantom approved document
      const { data: newDoc, error: docError } = await supabase
        .from('documents')
        .insert({
          requirement_id: requirementId,
          file_location: `phantom/${folderName}/${requirementId}/${Date.now()}.${fileExtension}`,
          original_filename: `${docType}_FANTASMA_${monthAbbrevs[month-1]}_${year}.${fileExtension}`,
          mime_type: 'application/octet-stream',
          file_size_bytes: 0,
          status: 'approved' as const,
          approved_by_user_id: user?.id,
          approval_timestamp: new Date().toISOString(),
          applicable_month: monthAbbrevs[month-1] as any,
          applicable_year: year,
          issue_date: `${year}-${month.toString().padStart(2, '0')}-01`
        } as any)
        .select()
        .single();

      if (docError) throw docError;

      // Update requirement to fulfilled (also when reusing an existing one,
      // which may still be in 'required' status)
      const { error: reqError } = await supabase
        .from('user_document_requirements')
        .update({ status: 'fulfilled' })
        .eq('id', requirementId);

      if (reqError) throw reqError;

      // Refresh data
      await fetchMonthFilteredRequirements(selectedMonth!);

      alert(`Documento ${docType} aprobado sin archivo exitosamente`);
    } catch (error) {
      console.error('Error creating phantom document:', error);
      alert('Error al aprobar documento sin archivo');
    } finally {
      setProcessingDoc(null);
    }
  };

  const getStatusIcon = (status: string, rejectionReason?: string | null) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending_approval':
        // Check if it has rejection_reason (revision request)
        if (rejectionReason) {
          return <AlertCircle className="w-5 h-5 text-orange-500" />;
        }
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const handleCellClick = async (month: string, documentId?: string) => {
    if (!validMonth(month) || mutationLock.current) return;
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('selectedMonth', month); next.delete('view'); next.delete('requirement');
      if (documentId) next.set('documentId', documentId); else next.delete('documentId');
      return next;
    }, { state: null });
  };
  const handleBackToTable = () => {
    if (mutationLock.current) return;
    if (selectedMonth) setCalendarMonth(Number(selectedMonth.slice(5)) - 1);
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('view', 'calendar'); next.set('year', String(new Date().getFullYear() + calendarOffset)); next.delete('selectedMonth'); next.delete('documentId'); next.delete('requirement'); return next; }, { state: null });
  };

  const getRenewalText = (type: string, interval: any) => {
    switch (type) {
      case 'monthly':
        return 'Mensual';
      case 'yearly':
        return 'Anual';
      case 'custom_interval':
        if (typeof interval === 'string') {
          if (interval === '2 months' || interval === '2 mons') return 'Bimestral';
          if (interval === '3 months' || interval === '3 mons') return 'Trimestral';
          if (interval === '4 months' || interval === '4 mons') return 'Cuatrimestral';
          return `Cada ${interval}`;
        } else if (interval?.months) {
          if (interval.months === 2) return 'Bimestral';
          if (interval.months === 3) return 'Trimestral';
          if (interval.months === 4) return 'Cuatrimestral';
          return `Cada ${interval.months} meses`;
        }
        return 'Intervalo personalizado';
      case 'fixed_date':
        return 'Fecha fija';
      default:
        return 'Sin renovación';
    }
  };
  
  const monthAbbrevToNumber = (monthAbbrev: string | null): number | null => {
    if (!monthAbbrev) return null;
    const months: Record<string, number> = {
      'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4,
      'MAY': 5, 'JUN': 6, 'JUL': 7, 'AUG': 8,
      'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
    };
    return months[monthAbbrev] || null;
  };

  const exportToPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const selectedYear = new Date().getFullYear() + calendarOffset;

    // Header section with better layout
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Calendario Anual ${selectedYear}`, pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${userDetail?.full_name || 'Usuario'}`, pageWidth / 2, 22, { align: 'center' });

    // Info box on the right
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}`, pageWidth - 15, 12, { align: 'right' });
    doc.text(`Email: ${userDetail?.email || ''}`, pageWidth - 15, 17, { align: 'right' });
    if (userDetail?.organization?.name || userDetail?.primaryOrganization?.name) {
      doc.text(`Org: ${userDetail?.organization?.name || userDetail?.primaryOrganization?.name}`, pageWidth - 15, 22, { align: 'right' });
    }

    // Prepare table data grouped by category - MATCHING UI LOGIC
    const MONTH_LABELS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const headers = ['Documento', ...MONTH_LABELS];

    const filteredStatuses = monthlyDocumentStatuses.filter((docStatus) => {
      if (!userDetail) return true;
      if (userDetail.is_moral === true) {
        return docStatus.isMoral === true || docStatus.isMoral === null || docStatus.isMoral === undefined;
      }
      if (userDetail.is_moral === false) {
        return docStatus.isMoral === false || docStatus.isMoral === null || docStatus.isMoral === undefined;
      }
      return true;
    });

    const tableData: any[] = [];
    let currentCategory = '';

    filteredStatuses.forEach(docStatus => {
      // Add category separator row
      if (docStatus.categoryName !== currentCategory) {
        currentCategory = docStatus.categoryName;
        tableData.push([{
          content: currentCategory,
          colSpan: 13,
          styles: {
            fillColor: [241, 245, 249],
            textColor: [30, 41, 59],
            fontStyle: 'bold',
            fontSize: 9,
            cellPadding: { top: 3, bottom: 3, left: 3, right: 3 }
          }
        }]);
      }

      // Build row using same logic as UI - respecting renewal types
      const row: any[] = [docStatus.documentName];
      const months = Object.keys(docStatus.monthStatuses).sort();
      const renewalType = docStatus.renewalType;
      const renewalInterval = docStatus.renewalInterval;

      // Calculate cells with proper period spanning (matching UI logic)
      const getCellData = () => {
        const cells: any[] = [];
        const statusPriority: Record<string, number> = {
          'approved': 4,
          'pending_approval': 2,
          'rejected': 1,
          'reemplazado': 0,
          'not_uploaded': 0
        };

        if (!renewalType || renewalType === 'monthly' || renewalType === 'none') {
          // Monthly: each cell is separate
          for (let i = 0; i < 12; i++) {
            const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
            const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
            cells.push({ status, span: 1 });
          }
        } else if (renewalType === 'yearly') {
          // Yearly: merge all 12 months, pick best status
          let bestStatus = 'not_uploaded';
          let bestPriority = 0;
          for (let i = 0; i < 12; i++) {
            const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
            const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
            const priority = statusPriority[status] || 0;
            if (priority > bestPriority) {
              bestStatus = status;
              bestPriority = priority;
            }
          }
          cells.push({ status: bestStatus, span: 12 });
        } else if (renewalType === 'custom_interval' && renewalInterval) {
          // Bimonthly, quarterly, etc: group by period
          let intervalMonths = 0;
          if (typeof renewalInterval === 'string') {
            const match = renewalInterval.match(/(\d+)\s*mons?/);
            if (match) intervalMonths = parseInt(match[1]);
          } else if (renewalInterval?.months) {
            intervalMonths = renewalInterval.months;
          }

          if (intervalMonths > 0) {
            for (let monthNum = 0; monthNum < 12; monthNum += intervalMonths) {
              let bestStatus = 'not_uploaded';
              let bestPriority = 0;
              const periodEnd = Math.min(monthNum + intervalMonths, 12);

              for (let i = monthNum; i < periodEnd; i++) {
                const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
                const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
                const priority = statusPriority[status] || 0;
                if (priority > bestPriority) {
                  bestStatus = status;
                  bestPriority = priority;
                }
              }
              cells.push({ status: bestStatus, span: periodEnd - monthNum });
            }
          } else {
            // Fallback to monthly
            for (let i = 0; i < 12; i++) {
              const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
              const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
              cells.push({ status, span: 1 });
            }
          }
        } else {
          // Fallback to monthly
          for (let i = 0; i < 12; i++) {
            const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
            const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
            cells.push({ status, span: 1 });
          }
        }

        return cells;
      };

      const cellData = getCellData();
      const statusMap: Record<string, string> = {
        'approved': '✓',
        'pending_approval': '⏳',
        'rejected': '✗',
        'not_uploaded': '—',
        'reemplazado': '✓'
      };

      // Add cells with proper colspan to merge cells like the UI
      cellData.forEach(cell => {
        const statusSymbol = statusMap[cell.status] || '—';
        if (cell.span > 1) {
          // Use colSpan to merge cells
          row.push({
            content: statusSymbol,
            colSpan: cell.span,
            styles: { halign: 'center' }
          });
        } else {
          row.push(statusSymbol);
        }
      });

      tableData.push(row);
    });

    // Add table with autoTable
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 30,
      styles: {
        fontSize: 7,
        cellPadding: 2.5,
        halign: 'center',
        valign: 'middle',
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 65, fontSize: 7 },
        1: { cellWidth: 16 }, 2: { cellWidth: 16 }, 3: { cellWidth: 16 }, 4: { cellWidth: 16 },
        5: { cellWidth: 16 }, 6: { cellWidth: 16 }, 7: { cellWidth: 16 }, 8: { cellWidth: 16 },
        9: { cellWidth: 16 }, 10: { cellWidth: 16 }, 11: { cellWidth: 16 }, 12: { cellWidth: 16 },
      },
      didParseCell: function(data: any) {
        // Handle category rows
        if (data.section === 'body' && data.cell.raw && typeof data.cell.raw === 'object' &&
            data.cell.raw.colSpan === 13 && data.cell.raw.content) {
          // This is a category row - already styled
          return;
        }

        if (data.column.index >= 1 && data.section === 'body') {
          // Get the value - handle both string and object cells
          let value = data.cell.text[0];

          // If cell is an object with content (merged cell)
          if (data.cell.raw && typeof data.cell.raw === 'object' && data.cell.raw.content) {
            value = data.cell.raw.content;
          }

          if (value === '✓') {
            data.cell.styles.fillColor = [34, 197, 94];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
          } else if (value === '⏳') {
            data.cell.styles.fillColor = [234, 179, 8];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
          } else if (value === '✗') {
            data.cell.styles.fillColor = [239, 68, 68];
            data.cell.styles.textColor = 255;
            data.cell.styles.fontStyle = 'bold';
          } else if (value === '—') {
            data.cell.styles.fillColor = [241, 245, 249];
            data.cell.styles.textColor = [148, 163, 184];
          }
        }
      },
      margin: { top: 30, left: 10, right: 10, bottom: 30 },
    });

    // Footer section with improved legend
    const finalY = (doc as any).lastAutoTable?.finalY || 50;

    // Check if we need a new page for legend
    if (finalY > pageHeight - 40) {
      doc.addPage();
    }

    const legendY = (doc as any).lastAutoTable?.finalY > pageHeight - 40 ? 20 : finalY + 10;

    // Legend title
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Leyenda:', 15, legendY);

    // Legend items with colored boxes
    const legendItems = [
      { symbol: '✓', text: 'Aprobado', color: [34, 197, 94], textColor: [255, 255, 255] },
      { symbol: '⏳', text: 'Pendiente', color: [234, 179, 8], textColor: [255, 255, 255] },
      { symbol: '✗', text: 'Rechazado', color: [239, 68, 68], textColor: [255, 255, 255] },
      { symbol: '—', text: 'No Subido', color: [241, 245, 249], textColor: [148, 163, 184] }
    ];

    let legendX = 15;
    const legendItemY = legendY + 8;

    legendItems.forEach((item, index) => {
      // Draw colored box with symbol
      doc.setFillColor(item.color[0], item.color[1], item.color[2]);
      doc.roundedRect(legendX, legendItemY - 4, 8, 6, 1, 1, 'F');

      // Draw symbol
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(item.textColor[0], item.textColor[1], item.textColor[2]);
      doc.text(item.symbol, legendX + 4, legendItemY + 1, { align: 'center' });

      // Draw text
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(item.text, legendX + 10, legendItemY + 1);

      legendX += 45;
    });

    // Footer info
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    const footerY = pageHeight - 10;
    doc.text('REPSE Platform - Documento generado automáticamente', 15, footerY);
    doc.text(`Página 1 de 1`, pageWidth - 15, footerY, { align: 'right' });

    // Save the PDF
    const fileName = `calendario-${userDetail?.full_name?.replace(/\s+/g, '-').toLowerCase() || 'usuario'}-${selectedYear}.pdf`;
    doc.save(fileName);
  };

  const exportToCSV = () => {
    const selectedYear = new Date().getFullYear() + calendarOffset;
    const MONTH_LABELS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

    // Create CSV header
    let csvContent = `Calendario Anual ${selectedYear} - ${userDetail?.full_name || 'Usuario'}\n`;
    csvContent += `Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}\n\n`;
    csvContent += `Documento,${MONTH_LABELS.join(',')}\n`;

    // Add data rows - MATCHING UI LOGIC
    const filteredStatuses = monthlyDocumentStatuses.filter((docStatus) => {
      if (!userDetail) return true;
      if (userDetail.is_moral === true) {
        return docStatus.isMoral === true || docStatus.isMoral === null || docStatus.isMoral === undefined;
      }
      if (userDetail.is_moral === false) {
        return docStatus.isMoral === false || docStatus.isMoral === null || docStatus.isMoral === undefined;
      }
      return true;
    });

    let currentCategory = '';
    filteredStatuses.forEach(docStatus => {
      // Add category separator
      if (docStatus.categoryName !== currentCategory) {
        currentCategory = docStatus.categoryName;
        csvContent += `\n"${currentCategory}"\n`;
      }

      const statusMap: Record<string, string> = {
        'approved': 'Aprobado',
        'pending_approval': 'Pendiente',
        'rejected': 'Rechazado',
        'not_uploaded': 'No Subido',
        'reemplazado': 'Reemplazado'
      };

      const statusPriority: Record<string, number> = {
        'approved': 4,
        'pending_approval': 2,
        'rejected': 1,
        'reemplazado': 0,
        'not_uploaded': 0
      };

      const renewalType = docStatus.renewalType;
      const renewalInterval = docStatus.renewalInterval;
      const row: string[] = [`"${docStatus.documentName}"`];

      // Use same period logic as PDF
      if (!renewalType || renewalType === 'monthly' || renewalType === 'none') {
        // Monthly
        for (let i = 0; i < 12; i++) {
          const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
          const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
          row.push(statusMap[status] || 'No Subido');
        }
      } else if (renewalType === 'yearly') {
        // Yearly: pick best status from all 12 months
        let bestStatus = 'not_uploaded';
        let bestPriority = 0;
        for (let i = 0; i < 12; i++) {
          const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
          const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
          const priority = statusPriority[status] || 0;
          if (priority > bestPriority) {
            bestStatus = status;
            bestPriority = priority;
          }
        }
        const statusText = statusMap[bestStatus] || 'No Subido';
        // Repeat status for all 12 months
        for (let i = 0; i < 12; i++) {
          row.push(statusText);
        }
      } else if (renewalType === 'custom_interval' && renewalInterval) {
        // Bimonthly, quarterly, etc
        let intervalMonths = 0;
        if (typeof renewalInterval === 'string') {
          const match = renewalInterval.match(/(\d+)\s*mons?/);
          if (match) intervalMonths = parseInt(match[1]);
        } else if (renewalInterval?.months) {
          intervalMonths = renewalInterval.months;
        }

        if (intervalMonths > 0) {
          let monthsProcessed = 0;
          for (let monthNum = 0; monthNum < 12; monthNum += intervalMonths) {
            let bestStatus = 'not_uploaded';
            let bestPriority = 0;
            const periodEnd = Math.min(monthNum + intervalMonths, 12);

            for (let i = monthNum; i < periodEnd; i++) {
              const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
              const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
              const priority = statusPriority[status] || 0;
              if (priority > bestPriority) {
                bestStatus = status;
                bestPriority = priority;
              }
            }

            const statusText = statusMap[bestStatus] || 'No Subido';
            // Repeat status for the period
            for (let i = monthNum; i < periodEnd; i++) {
              row.push(statusText);
              monthsProcessed++;
            }
          }
        } else {
          // Fallback to monthly
          for (let i = 0; i < 12; i++) {
            const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
            const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
            row.push(statusMap[status] || 'No Subido');
          }
        }
      } else {
        // Fallback to monthly
        for (let i = 0; i < 12; i++) {
          const monthKey = `${selectedYear}-${String(i + 1).padStart(2, '0')}`;
          const status = docStatus.monthStatuses[monthKey]?.status || 'not_uploaded';
          row.push(statusMap[status] || 'No Subido');
        }
      }

      csvContent += row.join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const fileName = `calendario-${userDetail?.full_name?.replace(/\s+/g, '-').toLowerCase() || 'usuario'}-${selectedYear}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !userDetail) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!userDetail) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="text-center">
          <p role="alert" className="text-gray-600">{detailError || 'Usuario no disponible'}</p><button onClick={fetchUserDetails} className="mt-4 rounded-lg border px-4 py-2 text-sm">Reintentar expediente</button>
          <button
            onClick={() => navigate('/review')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Volver a la lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen px-4 text-slate-900 sm:px-6 ${viewMode === 'list' ? 'review-page' : 'py-6 lg:px-8'}`}>
      <div className={`expediente ${viewMode === 'list' ? 'focused-expediente' : ''}`}>
        {viewMode === 'list' ? <ReviewContextHeader
          name={userDetail.full_name || userDetail.email}
          email={userDetail.email}
          organization={userDetail.organization?.name}
          month={selectedMonth || ''}
          busy={!!processingDoc || loading}
          backLabel={returnLabel(queueReturnTo)}
          canDownload={userProfile?.role === 'admin' || userProfile?.role === 'reviewer'}
          onBack={() => navigate(queueReturnTo || '/admin/users')}
          onMonth={handleCellClick}
          onCoverage={handleBackToTable}
          onRefresh={() => { fetchUserDetails(); if (selectedMonth) fetchMonthFilteredRequirements(selectedMonth); }}
          onDownload={() => setShowZipModal(true)}
          onNotifications={() => setShowNotificationHistory(true)}
        /> : <>
        {/* Back button - floating above user header */}
        <div className="mb-4">
          <button
            disabled={!!processingDoc} onClick={() => navigate(queueReturnTo || '/admin/users')}
            className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-medium transition-all duration-200 hover:translate-x-[-2px]"
          >
            <ArrowLeft className="w-4 h-4" />
            {returnLabel(queueReturnTo)}
          </button>
        </div>

        <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0"><h1 title={userDetail.full_name || userDetail.email} className="mt-1 break-words text-2xl font-semibold tracking-tight">{userDetail.full_name || userDetail.email}</h1><p className="mt-2 break-words text-sm text-slate-600">{userDetail.email} · {userDetail.organization?.name || 'Sin organización'}</p></div>
          <div className="flex flex-wrap items-center gap-3"><details className="relative"><summary className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">Más opciones</summary><div className="absolute right-0 z-20 mt-2 w-56 space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"><button disabled={!!processingDoc || loading} onClick={() => { fetchUserDetails(); if (selectedMonth) fetchMonthFilteredRequirements(selectedMonth); }} className="block p-2 text-sm text-blue-700 disabled:opacity-50">Actualizar expediente</button>{(userProfile?.role === 'admin' || userProfile?.role === 'reviewer') && <button onClick={() => setShowZipModal(true)} className="block p-2 text-sm text-blue-700">Descargar ZIP</button>}<button onClick={() => setShowNotificationHistory(true)} className="block p-2 text-sm text-blue-700">Historial de notificaciones</button></div></details></div>
        </header>
        </>}
        {detailError && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-white p-4 text-sm text-red-800">{detailError}</p>}
        {decisionMessage && <p role="status" className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">{decisionMessage}</p>}
        {viewMode === 'table' && <nav aria-label="Periodo y vista del expediente" className="expediente-tabs mb-5 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <p className="text-sm font-medium text-slate-600">Expediente de {new Date().getFullYear() + calendarOffset}</p>
          <button disabled={!!processingDoc} onClick={() => handleCellClick(selectedMonth || `${new Date().getFullYear() + calendarOffset}-${String(calendarMonth + 1).padStart(2,'0')}`)} className="px-3 py-2 text-sm text-slate-500">Documentos del periodo</button>
        </nav>}

        {/* Monthly Document Status Table */}
        {viewMode === 'table' && monthlyDocumentStatuses.length > 0 && (
          <div className="coverage-surface mb-6">
            {/* Year Navigation and Export buttons - top */}
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 p-4">
              <div><h2 className="font-semibold">Cobertura anual</h2><p className="mt-1 text-xs text-slate-500">Selecciona un periodo para consultar sus archivos.</p></div>
              <div className="flex items-center gap-3">
                <button
                  onClick={exportToPDF}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                >
                  <Download className="w-4 h-4" />
                  Exportar PDF
                </button>
                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50"
                >
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </button>
              </div>

              <div className="flex items-center gap-1">
                {(() => {
                  const currentYear = new Date().getFullYear();
                  const selectedYear = currentYear + calendarOffset;

                  return (
                    <>
                      <button
                        onClick={() => {
                          if (selectedYear - 1 >= 2015) {
                            setCalendarOffset(calendarOffset - 1);
                            setSearchParams(previous=>{const next=new URLSearchParams(previous);next.set('year',String(selectedYear-1));return next;});
                          }
                        }}
                        aria-label="Año anterior" disabled={selectedYear - 1 < 2015 || !!processingDoc}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-30"
                      >
                        <ArrowLeft className="w-4 h-4" />
                      </button>

                      <div className="px-3 text-sm font-medium text-gray-700 min-w-[3rem] text-center">
                        {selectedYear}
                      </div>

                      <button
                        aria-label="Año siguiente" disabled={!!processingDoc} onClick={() => {setCalendarOffset(calendarOffset + 1);setSearchParams(previous=>{const next=new URLSearchParams(previous);next.set('year',String(selectedYear+1));return next;});}}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4 rotate-180" />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <label className="text-xs text-slate-600">Buscar documento<input type="search" value={calendarSearch} onChange={event=>setCalendarSearch(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm sm:w-72"/></label>
              <label className="text-xs text-slate-600 md:hidden">Mes de cobertura<select value={calendarMonth} onChange={event=>setCalendarMonth(Number(event.target.value))} className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm">{Array.from({length:12},(_,i)=><option key={i} value={i}>{format(new Date(2026,i,1),'MMMM',{locale:es})}</option>)}</select></label>
            </div>
            <div className="overflow-x-auto">
              <table className="coverage-table min-w-full table-fixed" aria-label="Cobertura de documentos por periodo">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-700 w-64">Documento</th>
                    {Array.from({ length: 12 }, (_, i) => {
                      const currentYear = new Date().getFullYear() + calendarOffset;
                      const date = new Date(currentYear, i, 1);
                      const monthKey = format(date, 'yyyy-MM');
                      
                      // Check if month is in the future
                      const today = new Date();
                      const currentActualYear = today.getFullYear();
                      const currentActualMonth = today.getMonth();
                      const isDisabledMonth = currentYear > currentActualYear ||
                                           (currentYear === currentActualYear && i >= currentActualMonth);
                      
                      return (
                        <th 
                          key={i} 
                          className={`text-center py-2 px-2 font-medium text-sm w-20 ${
                            isDisabledMonth
                              ? 'text-gray-400 cursor-not-allowed opacity-50'
                              : 'text-gray-700 cursor-pointer hover:bg-gray-50'
                          }`}

                        >
                          <button disabled={isDisabledMonth || !!processingDoc} onClick={()=>handleCellClick(monthKey)} aria-label={`Consultar ${format(date,'MMMM yyyy',{locale:es})}`} className="min-h-11 rounded px-2 py-2 capitalize">{format(date, 'MMM', { locale: es })}</button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let currentCategory = '';
                    return monthlyDocumentStatuses
                      .filter(row=>row.documentName.toLocaleLowerCase('es').includes(calendarSearch.toLocaleLowerCase('es')))
                      .filter((docStatus) => {
                        // FILTRO ESTRICTO: Solo mostrar documentos compatibles con el is_moral del usuario
                        if (!userDetail) return true;
                        
                        // Si el usuario es moral (true), mostrar solo documentos con is_moral true o null
                        if (userDetail.is_moral === true) {
                          return docStatus.isMoral === true || docStatus.isMoral === null || docStatus.isMoral === undefined;
                        }
                        
                        // Si el usuario es físico (false), mostrar solo documentos con is_moral false o null
                        if (userDetail.is_moral === false) {
                          return docStatus.isMoral === false || docStatus.isMoral === null || docStatus.isMoral === undefined;
                        }
                        
                        return true;
                      })
                      .map((docStatus) => {
                      const months = Object.keys(docStatus.monthStatuses).sort();
                      const rows = [];
                      
                      // Add category header if it's different from the previous one
                      if (docStatus.categoryName !== currentCategory) {
                        currentCategory = docStatus.categoryName;
                        rows.push(
                          <tr key={`category-${currentCategory}`} className="bg-gray-50">
                            <td colSpan={13} className="py-2 px-3 font-semibold text-gray-700 w-full">
                              {currentCategory}
                            </td>
                          </tr>
                        );
                      }
                      
                      // Add document row
                      const renewalType = docStatus.renewalType;
                      const renewalInterval = docStatus.renewalInterval;
                   
                      // Calculate cell spans for non-monthly documents
                      const getCellSpans = () => {
                        const spans: Array<{startIndex: number, span: number, status: any, periodStartMonth?: string, periodEndMonth?: string}> = [];
                        
                        // PRIMERO: Determinar la estructura de las celdas basada en renewal_type
                        if (!renewalType || renewalType === 'monthly' || renewalType === 'none') {
                          // Para documentos mensuales o sin tipo, cada mes es una celda separada
                          months.forEach((month, index) => {
                            spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
                          });
                        } else if (renewalType === 'yearly') {
                          // Para documentos anuales, fusionar los 12 meses del año
                          // Agrupar meses por año
                          const yearGroups: {[year: string]: {start: number, end: number}} = {};
                          
                          months.forEach((month, index) => {
                            const [yearStr] = month.split('-');
                            if (!yearGroups[yearStr]) {
                              yearGroups[yearStr] = {start: index, end: index};
                            } else {
                              yearGroups[yearStr].end = index;
                            }
                          });
                          
                          // Crear spans para cada año visible
                          Object.entries(yearGroups).forEach(([year, indices]) => {
                            const yearMonths = months.slice(indices.start, indices.end + 1);
                            // Get the best status in the year (approved > pending_approval > rejected > not_uploaded)
                            const statusPriority: Record<string, number> = {
                              'approved': 4,
                              'pending_approval': 2,
                              'rejected': 1,
                              'reemplazado': 0,
                              'not_uploaded': 0
                            };
                            let bestStatus = docStatus.monthStatuses[yearMonths[0]];
                            yearMonths.forEach(month => {
                              const monthStatus = docStatus.monthStatuses[month];
                              if (statusPriority[monthStatus.status] > statusPriority[bestStatus.status]) {
                                bestStatus = monthStatus;
                              }
                            });
                            const yearStatus = bestStatus;
                            
                            spans.push({
                              startIndex: indices.start,
                              span: indices.end - indices.start + 1,
                              status: yearStatus,
                              periodStartMonth: `${year}-01` // Enero del año
                            });
                          });
                        } else if (renewalType === 'custom_interval' && renewalInterval) {
                          // Para intervalos personalizados, crear períodos desde enero
                          let intervalMonths = 0;
                          if (typeof renewalInterval === 'string') {
                            const match = renewalInterval.match(/(\d+)\s*mons?/);
                            if (match) {
                              intervalMonths = parseInt(match[1]);
                            }
                          } else if (renewalInterval?.months) {
                            intervalMonths = renewalInterval.months;
                          }
                          
                          if (intervalMonths > 0) {
                            // Procesar meses secuencialmente para crear períodos
                            let i = 0;
                            while (i < months.length) {
                              const monthStr = months[i]; // "2024-01", "2024-02", etc.
                              const [yearStr, monthNumStr] = monthStr.split('-');
                              const year = parseInt(yearStr);
                              const monthNum = parseInt(monthNumStr) - 1; // Convertir a 0-11 para el cálculo
                              
                              // Calcular el período al que pertenece este mes
                              // Para bimestral: 0-1 (ene-feb), 2-3 (mar-abr), 4-5 (may-jun), etc.
                              const periodIndex = Math.floor(monthNum / intervalMonths);
                              const periodStartMonth = periodIndex * intervalMonths;
                              const periodEndMonth = Math.min(periodStartMonth + intervalMonths - 1, 11);
                              
                              // Recolectar todos los meses consecutivos del mismo período y año
                              let spanEnd = i;
                              while (spanEnd < months.length) {
                                const nextMonthStr = months[spanEnd];
                                const [nextYearStr, nextMonthNumStr] = nextMonthStr.split('-');
                                const nextYear = parseInt(nextYearStr);
                                const nextMonthNum = parseInt(nextMonthNumStr) - 1; // 0-11
                                
                                if (nextYear !== year || 
                                    nextMonthNum < periodStartMonth || 
                                    nextMonthNum > periodEndMonth) {
                                  break;
                                }
                                spanEnd++;
                              }
                              
                              // Crear el span para este período
                              const periodMonths = months.slice(i, spanEnd);
                              // Get the best status in the period (approved > pending_approval > rejected > not_uploaded)
                              const statusPriority: Record<string, number> = {
                                'approved': 4,
                                'pending_approval': 2,
                                'rejected': 1,
                                'reemplazado': 0,
                                'not_uploaded': 0
                              };
                              let bestStatus = docStatus.monthStatuses[periodMonths[0]];
                              periodMonths.forEach(month => {
                                const monthStatus = docStatus.monthStatuses[month];
                                if (statusPriority[monthStatus.status] > statusPriority[bestStatus.status]) {
                                  bestStatus = monthStatus;
                                }
                              });
                              const periodStatus = bestStatus;
                              
                              spans.push({
                                startIndex: i,
                                span: spanEnd - i,
                                status: periodStatus,
                                periodStartMonth: `${year}-${String(periodStartMonth + 1).padStart(2, '0')}`,
                                periodEndMonth: `${year}-${String(periodEndMonth + 1).padStart(2, '0')}`
                              });
                              
                              i = spanEnd;
                            }
                          } else {
                            // Intervalo inválido, mostrar todo como celdas separadas
                            months.forEach((month, index) => {
                              spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
                            });
                          }
                        } else if (renewalType === 'fixed_date') {
                          // Para fecha fija, buscar el mes con el documento
                          const uploadedMonth = months.findIndex(month => 
                            docStatus.monthStatuses[month].status !== 'not_uploaded'
                          );
                          if (uploadedMonth >= 0) {
                            spans.push({
                              startIndex: uploadedMonth,
                              span: 1,
                              status: docStatus.monthStatuses[months[uploadedMonth]]
                            });
                            // Llenar el resto con celdas vacías
                            for (let i = 0; i < months.length; i++) {
                              if (i !== uploadedMonth) {
                                spans.push({startIndex: i, span: 1, status: docStatus.monthStatuses[months[i]]});
                              }
                            }
                            // Sort by startIndex
                            spans.sort((a, b) => a.startIndex - b.startIndex);
                          } else {
                            // No documents uploaded, show all as not uploaded
                            months.forEach((month, index) => {
                              spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
                            });
                          }
                        } else {
                          // Default to monthly behavior
                          months.forEach((month, index) => {
                            spans.push({startIndex: index, span: 1, status: docStatus.monthStatuses[month]});
                          });
                        }
                        
                        // Sort spans by startIndex to ensure proper order
                        spans.sort((a, b) => a.startIndex - b.startIndex);
                        
                        return spans;
                      };
                      
                      const cellSpans = getCellSpans();
                      
                      rows.push(
                        <tr key={docStatus.requirementId} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-3 pl-6 font-medium text-sm w-64">
                            <div className="flex items-center gap-2">
                              <span className="break-words">{docStatus.documentName}</span>
                              {docStatus.isMoral === true && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap bg-purple-100 text-purple-700">
                                  Exclusivo Moral
                                </span>
                              )}
                              {docStatus.isMoral === false && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap bg-blue-100 text-blue-700">
                                  Exclusivo Físico
                                </span>
                              )}
                            </div>
                          </td>
                          {cellSpans.map((spanInfo, idx) => {
                            const status = spanInfo.status;
                            // Para custom_interval, usar el último mes del período, para otros usar el primero
                            const clickMonth = (renewalType === 'custom_interval' && spanInfo.periodEndMonth)
                              ? spanInfo.periodEndMonth
                              : (spanInfo.periodStartMonth || months[spanInfo.startIndex]);

                            // Check if this month is in the future or current month
                            const [yearStr, monthStr] = clickMonth.split('-');
                            const cellYear = parseInt(yearStr);
                            const cellMonth = parseInt(monthStr) - 1; // Convert to 0-based month
                            const today = new Date();
                            const currentActualYear = today.getFullYear();
                            const currentActualMonth = today.getMonth();

                            // Check if document exists (has been uploaded)
                            const hasDocument = status.status !== 'not_uploaded' && status.status !== 'reemplazado';

                            // Disable month only if it's future/current AND no document exists
                            const isDisabledMonth = !hasDocument && (cellYear > currentActualYear ||
                                                 (cellYear === currentActualYear && cellMonth >= currentActualMonth));

                            // Para custom_interval: verificar si estamos en el mes siguiente al período
                            let isBlockedCustomInterval = false;
                            if (renewalType === 'custom_interval' && renewalInterval && spanInfo.span > 1) {
                              // Obtener el primer mes del período
                              const firstMonth = months[spanInfo.startIndex];
                              const [firstYearStr, firstMonthStr] = firstMonth.split('-');
                              const firstCellYear = parseInt(firstYearStr);
                              const firstCellMonth = parseInt(firstMonthStr) - 1; // 0-11

                              // Extraer el número de meses del intervalo
                              let intervalMonths = 0;
                              if (typeof renewalInterval === 'string') {
                                const match = renewalInterval.match(/(\d+)\s*mons?/);
                                if (match) {
                                  intervalMonths = parseInt(match[1]);
                                }
                              } else if (renewalInterval?.months) {
                                intervalMonths = renewalInterval.months;
                              }

                              // Bloquear si currentActualMonth < n + intervalMonths (sin importar si hay documento)
                              // Ejemplo: Para ene-feb (n=0, interval=2), bloquear si currentMonth < 0+2 (marzo)
                              if (!isDisabledMonth && intervalMonths > 0) {
                                // Calcular si el mes actual está antes del mes de habilitación
                                const enableMonth = firstCellMonth + intervalMonths; // mes en que se habilita (0-11)

                                // Ajustar si el mes de habilitación cruza al año siguiente
                                const enableYear = enableMonth > 11 ? firstCellYear + 1 : firstCellYear;
                                const enableMonthAdjusted = enableMonth > 11 ? enableMonth - 12 : enableMonth;

                                const isBeforeEnableDate = (currentActualYear < enableYear) ||
                                                          (currentActualYear === enableYear && currentActualMonth < enableMonthAdjusted);

                                if (isBeforeEnableDate) {
                                  isBlockedCustomInterval = true;
                                }
                              }
                            }

                            let cellClass = 'text-center p-0 ';
                            let cellContent = '';
                            let isClickable = !isDisabledMonth && !isBlockedCustomInterval;
                            let titleText = '';

                            // Verificar primero si está bloqueado
                            if (isDisabledMonth) {
                              // Current and future months are disabled
                              if (hasDocument) {
                                // Si hay documento, mostrar su color con opacidad
                                switch (status.status) {
                                  case 'approved':
                                    cellClass += 'bg-green-100 opacity-50';
                                    cellContent = '✓';
                                    titleText = 'Mes actual o futuro - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                  case 'pending_approval':
                                    if (status.rejectionReason) {
                                      cellClass += 'bg-orange-100 opacity-50';
                                      cellContent = '⚠️';
                                    } else {
                                      cellClass += 'bg-yellow-100 opacity-50';
                                      cellContent = '⏳';
                                    }
                                    titleText = 'Mes actual o futuro - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                  case 'rejected':
                                    cellClass += 'bg-red-100 opacity-50';
                                    cellContent = '✗';
                                    titleText = 'Mes actual o futuro - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                }
                              } else {
                                cellClass += 'bg-gray-50 opacity-50';
                                cellContent = '-';
                                titleText = 'Mes actual o futuro';
                              }
                            } else if (isBlockedCustomInterval) {
                              // Blocked custom interval months - mostrar color con opacidad si hay documento
                              if (hasDocument) {
                                // Si hay documento, mostrar su color con opacidad
                                switch (status.status) {
                                  case 'approved':
                                    cellClass += 'bg-green-100 opacity-50';
                                    cellContent = '✓';
                                    titleText = 'Periodo no disponible aún - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                  case 'pending_approval':
                                    if (status.rejectionReason) {
                                      cellClass += 'bg-orange-100 opacity-50';
                                      cellContent = '⚠️';
                                    } else {
                                      cellClass += 'bg-yellow-100 opacity-50';
                                      cellContent = '⏳';
                                    }
                                    titleText = 'Periodo no disponible aún - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                  case 'rejected':
                                    cellClass += 'bg-red-100 opacity-50';
                                    cellContent = '✗';
                                    titleText = 'Periodo no disponible aún - ' + (status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '');
                                    break;
                                }
                              } else {
                                cellClass += 'bg-gray-50 opacity-50';
                                cellContent = '-';
                                titleText = 'Periodo no disponible aún';
                              }
                            } else if (hasDocument && (cellYear === currentActualYear && cellMonth >= currentActualMonth)) {
                              // Documento existe en mes actual o futuro, mostrar su estado normalmente
                              switch (status.status) {
                                case 'approved':
                                  cellClass += 'bg-green-100';
                                  cellContent = '✓';
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                                case 'pending_approval':
                                  if (status.rejectionReason) {
                                    cellClass += 'bg-orange-100';
                                    cellContent = '⚠️';
                                  } else {
                                    cellClass += 'bg-yellow-100';
                                    cellContent = '⏳';
                                  }
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                                case 'rejected':
                                  cellClass += 'bg-red-100';
                                  cellContent = '✗';
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                              }
                            } else {
                              switch (status.status) {
                                case 'not_uploaded':
                                case 'reemplazado':
                                  cellClass += 'bg-gray-100';
                                  cellContent = '-';
                                  titleText = 'Click para subir documento';
                                  break;
                                case 'approved':
                                  cellClass += 'bg-green-100';
                                  cellContent = '✓';
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                                case 'pending_approval':
                                  // Check if it has rejection_reason (revision request)
                                  if (status.rejectionReason) {
                                    cellClass += 'bg-orange-100';
                                    cellContent = '⚠️';
                                  } else {
                                    cellClass += 'bg-yellow-100';
                                    cellContent = '⏳';
                                  }
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                                case 'rejected':
                                  cellClass += 'bg-red-100';
                                  cellContent = '✗';
                                  titleText = status.uploadDate ? `Subido: ${format(new Date(status.uploadDate), 'dd/MM/yyyy')}` : '';
                                  break;
                              }
                            }

                            return (
                              <td
                                key={`${clickMonth}-${idx}`}
                                colSpan={spanInfo.span}
                                className={`${cellClass} ${spanInfo.span > 1 ? 'border-x border-gray-300' : ''} ${
                                  isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'
                                }`}
                                title={titleText}
                                data-mobile-visible={calendarMonth >= spanInfo.startIndex && calendarMonth < spanInfo.startIndex + spanInfo.span}
                              >
                                <button disabled={!isClickable || !!processingDoc} onClick={()=>handleCellClick(clickMonth,status.documentId)} aria-label={`${docStatus.documentName}, ${clickMonth}: ${status.status === 'approved' ? 'Aprobado' : status.status === 'rejected' ? 'Rechazado' : status.status === 'pending_approval' ? (status.rejectionReason ? 'Revisión solicitada' : 'Por revisar') : 'Sin subir'}${!isClickable ? ', periodo no disponible' : ''}`}>
                                  <span aria-hidden="true" className="text-sm font-semibold">{cellContent === '⏳' ? '●' : cellContent === '⚠️' ? '!' : cellContent}</span>
                                  <span className="ml-2 text-xs md:hidden">{status.status === 'approved' ? 'Aprobado' : status.status === 'rejected' ? 'Rechazado' : status.status === 'pending_approval' ? (status.rejectionReason ? 'Revisión solicitada' : 'Por revisar') : 'Sin subir'}{!isClickable ? ' · No disponible' : ''}</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                      
                      return rows;
                    });
                  })()}
                  {calendarSearch && !monthlyDocumentStatuses.some(row=>row.documentName.toLocaleLowerCase('es').includes(calendarSearch.toLocaleLowerCase('es'))) && <tr><td colSpan={13} className="p-5 text-sm text-slate-600">No hay documentos que coincidan con la búsqueda.</td></tr>}
                </tbody>
              </table>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-200 px-4 py-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 rounded"></div>
                  <span className="text-gray-600">Sin subir</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 rounded"></div>
                  <span className="text-gray-600">Aprobado</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-100 rounded"></div>
                  <span className="text-gray-600">Pendiente de revisión</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-100 rounded"></div>
                  <span className="text-gray-600">Revisión solicitada</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-100 rounded"></div>
                  <span className="text-gray-600">Rechazado</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending Documents Alert - Only in table view */}
        {viewMode === 'table' && documents.filter(d => d && d.status === 'pending_approval').length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <button
              onClick={() => setShowPendingList(!showPendingList)}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <span className="text-blue-800 font-medium">
                  {documents.filter(d => d && d.status === 'pending_approval').length} documento(s) pendiente(s) de revisión
                </span>
              </div>
              {showPendingList ? (
                <ChevronDown className="w-5 h-5 text-blue-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-blue-600" />
              )}
            </button>
            {showPendingList && (
              <div className="mt-3 border-t border-blue-200 pt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-blue-700">
                      <th className="pb-2 font-medium">Documento</th>
                      <th className="pb-2 font-medium">Archivo</th>
                      <th className="pb-2 font-medium">Mes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.filter(d => d && d.status === 'pending_approval').map((doc: any) => (
                      <tr
                        key={doc.id}
                        className="border-t border-blue-100 hover:bg-blue-100 cursor-pointer transition-colors"
                        onClick={() => {
                          const monthStr = filePeriod(doc);
                          if (monthStr) {
                            handleCellClick(monthStr, doc.id);
                          }
                        }}
                      >
                        <td className="py-2 text-blue-900">{doc.requirement?.definition?.name || 'Sin nombre'}</td>
                        <td className="py-2 text-blue-800">{doc.original_filename}</td>
                        <td className="py-2 text-blue-800">
                          {doc.requirement?.due_date
                            ? format(new Date(doc.requirement.due_date), 'MMMM yyyy', { locale: es })
                            : 'Sin fecha'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}

        {viewMode === 'list' && <ReviewWorkspace requirements={monthFilteredRequirements} loading={loadingMonthData} error={monthError} retry={() => { if (selectedMonth) fetchMonthFilteredRequirements(selectedMonth); }} month={selectedMonth || ''} requestedDocumentId={routeDocumentId} requestedRequirementId={searchParams.get('requirement')} role={userProfile?.role} busy={!!processingDoc || loading || !!detailError} onSelect={(documentId, requirementId) => {
          setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('requirement', requirementId); if (documentId) next.set('documentId', documentId); else next.delete('documentId'); return next; }, { replace: true, state: null });
        }} onApprove={doc => { setDecisionError(null); setDocumentToApprove(doc); setShowApprovalDialog(true); }} onReject={(doc,aiSummary) => { setRejectionAiSummary(aiSummary); setDecisionError(null); setSelectedDoc(doc); setRejectionReason(''); setIsRevisionRequest(false); setShowRejectDialog(true); }} onRevision={doc => { setRejectionAiSummary(undefined); setDecisionError(null); setSelectedDoc(doc); setRejectionReason(''); setIsRevisionRequest(true); setShowRejectDialog(true); }} onDownload={handleDownload} onDelete={handleDelete} onDiscard={handleDiscardDocument} onOptionalApproval={handleCreatePhantomSUA} onOverride={openOverrideModal} periodicity={req => {
          const override = overrides.find(item => item.definition_id === req.definition.id);
          return override ? getOverrideLabel(override) : getRenewalText(req.definition.renewal_type || '', req.definition.renewal_interval);
        }} />}
      </div>

      {showRejectDialog && selectedDoc && <DecisionDialog aiSummary={rejectionAiSummary} action={isRevisionRequest ? 'revision' : 'reject'} doc={selectedDoc} person={userDetail.full_name || userDetail.email} scope={selectedMonth} busy={!!processingDoc} error={decisionError} reason={rejectionReason} onReason={setRejectionReason} onClose={() => { setShowRejectDialog(false); setSelectedDoc(null); setRejectionReason(''); setIsRevisionRequest(false); }} onConfirm={message => { if (isRevisionRequest && selectedDoc.status === 'approved') handleRequestRevision(); else handleReject(message); }} />}

      {/* Approval Confirmation Dialog */}
      {showZipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-1">
              {zipMode === 'annual' ? 'Descargar ZIP anual' : 'Descargar ZIP por mes'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {zipMode === 'annual'
                ? 'Se incluirán todos los documentos del año seleccionado.'
                : 'Selecciona el mes y año. Se incluirán todos los documentos aplicables a ese mes (mensuales, bimestrales, semestrales, anuales, etc.).'}
            </p>

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setZipMode('monthly')}
                disabled={downloadingZip}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  zipMode === 'monthly'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50`}
              >
                Por mes
              </button>
              <button
                type="button"
                onClick={() => setZipMode('annual')}
                disabled={downloadingZip}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  zipMode === 'annual'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50`}
              >
                Anual
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {zipMode === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
                  <select
                    value={zipMonth}
                    onChange={(e) => setZipMonth(parseInt(e.target.value, 10))}
                    disabled={downloadingZip}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
                      .map((m, i) => {
                        const monthNum = i + 1;
                        const isBlocked = zipYear === _currentYear && monthNum >= _currentMonth;
                        return (
                          <option key={i} value={monthNum} disabled={isBlocked}>
                            {m}
                          </option>
                        );
                      })}
                  </select>
                </div>
              )}
              <div className={zipMode === 'annual' ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                <select
                  value={zipYear}
                  onChange={(e) => setZipYear(parseInt(e.target.value, 10))}
                  disabled={downloadingZip}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {Array.from({ length: 6 }, (_, i) => _currentYear - i)
                    .map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  if (downloadingZip && zipAbortRef.current) zipAbortRef.current.abort();
                  setShowZipModal(false);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDownloadZip}
                disabled={downloadingZip}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {downloadingZip ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    {zipProgress?.phase === 'assembling'
                      ? 'Ensamblando ZIP...'
                      : zipProgress
                      ? `Preparando meses (${zipProgress.done}/${zipProgress.total})...`
                      : 'Generando...'}
                  </>
                ) : (
                  <><Download className="w-4 h-4" /> Descargar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {overrideModalDef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-1">Periodicidad personalizada</h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{overrideModalDef.name}</strong> para este usuario. Al aprobar un documento,
              los periodos restantes del intervalo se completarán automáticamente.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Periodicidad</label>
                <select
                  value={overrideForm.renewal_type}
                  onChange={(e) => setOverrideForm({ ...overrideForm, renewal_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="custom_interval">Cada N meses</option>
                  <option value="yearly">Anual</option>
                </select>
              </div>

              {overrideForm.renewal_type === 'custom_interval' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo (meses)</label>
                  <input
                    type="number"
                    min={2}
                    max={12}
                    value={overrideForm.interval_months}
                    onChange={(e) => setOverrideForm({ ...overrideForm, interval_months: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6">
              {overrides.some(o => o.definition_id === overrideModalDef.id) ? (
                <button
                  onClick={handleRemoveOverride}
                  disabled={savingOverride}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                >
                  Quitar personalización
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setOverrideModalDef(null)}
                  disabled={savingOverride}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveOverride}
                  disabled={savingOverride}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {savingOverride ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showApprovalDialog && documentToApprove && <DecisionDialog action="approve" doc={documentToApprove} person={userDetail.full_name || userDetail.email} scope={selectedMonth} busy={!!processingDoc} error={decisionError} reason="" onReason={() => {}} onClose={() => { setShowApprovalDialog(false); setDocumentToApprove(null); }} onConfirm={async () => { const success = await handleApprove(documentToApprove); if (success) { setShowApprovalDialog(false); setDocumentToApprove(null); } }} />}

      {/* Notification History Modal */}
      {showNotificationHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold">Historial de Notificaciones</h3>
              <button
                onClick={() => setShowNotificationHistory(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-6 overflow-y-auto">
              {notificationLogs.length > 0 ? (
                <div className="space-y-2">
                  {notificationLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        {log.email_status === 'sent' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {log.notification_type === 'status_change' && 'Cambio de estado'}
                            {log.notification_type === 'due_date_reminder' && 'Recordatorio de vencimiento'}
                            {log.notification_type === 'user_invitation' && 'Invitación de usuario'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {log.email_sent_at && format(new Date(log.email_sent_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </span>
                        </div>
                        {log.metadata && (
                          <p className="text-xs text-gray-600 mt-1">
                            {log.metadata.document_name && `Documento: ${log.metadata.document_name}`}
                            {log.metadata.status && ` - Estado: ${log.metadata.status}`}
                          </p>
                        )}
                        {log.error_message && (
                          <p className="text-xs text-red-600 mt-1">Error: {log.error_message}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No hay notificaciones registradas</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}